import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { FilterGroup } from '../../types';
import FilterConditionRow from './FilterConditionRow';

export default function FilterGroupBuilder({
  group,
  semanticModel,
  getDisplayName,
  depth = 0,
  isRoot = false,
}: {
  group: FilterGroup;
  semanticModel: any;
  getDisplayName: (name: string) => string;
  depth?: number;
  isRoot?: boolean;
}) {
  const addFilterCondition = useVoyagerStore((s) => s.addFilterCondition);
  const addFilterGroup = useVoyagerStore((s) => s.addFilterGroup);
  const removeFilterNode = useVoyagerStore((s) => s.removeFilterNode);
  const toggleFilterGroupLogic = useVoyagerStore((s) => s.toggleFilterGroupLogic);
  const toggleFilterGroupNegated = useVoyagerStore((s) => s.toggleFilterGroupNegated);
  const defaultFilterIds = useVoyagerStore((s) => s.defaultFilterIds);

  return (
    <Box
      sx={{
        border: isRoot ? 0 : 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: isRoot ? 0 : 1,
        ml: isRoot ? 0 : 1,
        mt: isRoot ? 0 : 0.5,
        bgcolor: isRoot ? 'transparent' : depth % 2 === 1 ? 'grey.50' : 'transparent',
        // .fgrp / .fgrp.nested — tokens, so a nested group reads in dark mode too.
        ...(!isRoot ? {
          borderColor: 'var(--line-soft)',
          borderRadius: '10px',
          ml: 1.75,
          bgcolor: depth % 2 === 1 ? 'var(--sunk)' : 'transparent',
        } : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Button
          size="small"
          variant={group.logic === 'AND' ? 'contained' : 'outlined'}
          onClick={() => toggleFilterGroupLogic(group.id)}
          sx={{ minWidth: 40, fontSize: 11, px: 1, py: 0.25, textTransform: 'none' }}
        >
          {group.logic}
        </Button>
        <Button
          size="small"
          variant={group.negated ? 'contained' : 'outlined'}
          color={group.negated ? 'error' : 'inherit'}
          onClick={() => toggleFilterGroupNegated(group.id)}
          sx={{ minWidth: 36, fontSize: 11, px: 1, py: 0.25, textTransform: 'none' }}
        >
          NOT
        </Button>
        <Tooltip title="Add condition">
          <IconButton size="small" onClick={() => addFilterCondition(group.id, '', '=', '')} sx={{ p: 0.25 }}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Add nested group">
          <Button
            size="small"
            variant="text"
            onClick={() => addFilterGroup(group.id)}
            sx={{ fontSize: 10, px: 0.5, py: 0.25, textTransform: 'none', minWidth: 0 }}
          >
            + Group
          </Button>
        </Tooltip>
        {!isRoot && (
          <IconButton size="small" onClick={() => removeFilterNode(group.id)} sx={{ p: 0.25, ml: 'auto' }}>
            <DeleteOutlineIcon fontSize="small" sx={{ color: 'text.disabled' }} />
          </IconButton>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {group.children.map((child) => {
          if (child.type === 'condition') {
            const isLocked = defaultFilterIds.has(child.id);
            return (
              <FilterConditionRow
                key={child.id}
                condition={child}
                semanticModel={semanticModel}
                getDisplayName={getDisplayName}
                onRemove={() => removeFilterNode(child.id)}
                locked={isLocked}
              />
            );
          }
          return (
            <FilterGroupBuilder
              key={child.id}
              group={child}
              semanticModel={semanticModel}
              getDisplayName={getDisplayName}
              depth={depth + 1}
            />
          );
        })}
        {group.children.length === 0 && (
          <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', ml: 1 }}>
            No conditions. Click + to add.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
